import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { TypeaheadMatch, TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Tenant } from '@parashift/shared/models';
import { AllowedSubtenants, JsonApiQueryData, NotificationService, Subtenant, TenantService } from '@parashift/shared/services';
import { ModalService } from '@parashift/shared/components';

@Component({
  selector: 'sub-tenants',
  templateUrl: './sub-tenants.component.html',
  styleUrls: ['./sub-tenants.component.scss'],
  imports: [
    FaIconComponent,
    FormsModule,
    ReactiveFormsModule,
    TypeaheadModule
],
})
export class SubTenantsComponent implements OnInit {
  private tenantService = inject(TenantService);
  private modalService = inject(ModalService);
  private notificationService = inject(NotificationService);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  @Input() currentTenant: Tenant;

  allowedSubTenants: Subtenant[] = [];
  currentSubTenants: any[] = [];
  show = false;
  searchTenant: any;
  hideNewTenant = true;

  ngOnInit() {
    this.getSubTenants();
  }

  onSelectTenant(event: TypeaheadMatch) {
    this.addSubTenantToTenant(event.item);
    this.resetTypeahead();
  }

  typeaheadOnBlur() {
    this.resetTypeahead();
  }

  unlinkTenant(index: number, tenant: Subtenant) {
    this.modalService.confirmation({
      initialState: {
        title: $localize `:@@sub_tenants.remove_subtenant_title:Remove sub-tenant`,
        description: $localize `:@@sub_tenants.remove_subtenant_description:Please confirm that you want to remove this sub-tenant.`
      }
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this.removeSubTenantFromTenant(index, tenant);
    });
  }

  private getSubTenants() {
    const multiRequests = forkJoin([
      this.tenantService.getAllowedSubTenants(this.currentTenant.id),
      this.tenantService.getCurrentSubTenants(this.currentTenant.id)
    ]);

    multiRequests.subscribe((response: [AllowedSubtenants, JsonApiQueryData<Tenant>]) => {
      this.allowedSubTenants = response[0] && response[0].allowed_subtenants;
      this.currentSubTenants = (response[1] && response[1].getModels()) || [];
    });
  }

  private resetTypeahead() {
    this.searchTenant = undefined;
  }

  private addSubTenantToTenant(tenant: Subtenant) {
    const index = this.allowedSubTenants.findIndex(pt => pt.id === tenant.id);

    if (index > -1) {
      this.tenantService.addSubTenant(this.currentTenant.id, tenant.id).subscribe({
        next: () => {
          this.allowedSubTenants.splice(index, 1);
          this.currentSubTenants.push(tenant);

          this.notificationService.success({
            title: $localize `:@@sub_tenants.notifications.add_tenant_success.title:Add tenant`,
            description: $localize `:@@sub_tenants.notifications.add_tenant_success.description:The subtenant <strong>${ tenant.name }:tenantName:</strong> has been successfully added`,
          });
        },
        error: () => {
          this.notificationService.error({
            title: $localize `:@@sub_tenants.notifications.add_tenant_error.title:Add tenant`,
            description: $localize `:@@sub_tenants.notifications.add_tenant_error.description:Adding the subtenant failed`
          });
        }
      });
    }
  }

  private removeSubTenantFromTenant(tenantIndex: number, tenant: Subtenant) {
    this.tenantService.removeSubTenant(this.currentTenant.id, tenant.id).subscribe({
      next: () => {
        this.currentSubTenants.splice(tenantIndex, 1);
        this.allowedSubTenants.push(tenant);

        this.notificationService.success({
          title: $localize `:@@sub_tenants.notifications.remove_tenant_success.title:Remove subtenant`,
          description: $localize `:@@sub_tenants.notifications.remove_tenant_success.description:The subtenant <strong>${ tenant.name }:tenantName:</strong> has been successfully removed`,
        });
      },
      error: () => {
        this.notificationService.error({
          title: $localize `:@@sub_tenants.notifications.remove_tenant_error.title:Remove subtenant`,
          description: $localize `:@@sub_tenants.notifications.remove_tenant_error.description:Removing the subtenant failed`
        });
      }
    });
  }
}
